import React, {useState} from 'react';
import {Box, Button, Divider, Grid, Typography} from '@mui/material';
import {useParams} from 'react-router-dom';
import {get} from 'lodash-es';
import {Warning} from '@mui/icons-material';
import {WizardField} from '../Wizard/QuestionWrapper';
import {getLabelOrValue} from '../AutoForm/AutoFieldNew';
import {useReviewContext} from '../../contexts/ReviewContext';
import {precursorAndHeaderFieldsType} from '../../pages/PrecursorWizard/fields/precursorFields';
import {SubmitRow} from './SubmitRow';
import {useAccountContext} from '../../contexts/AccountContext';
import {getBookedDate, getReviewAction} from '../../hooks/useUpdatePrecursor';
import {ReviewAction} from '../../schema-types/globalTypes';
import theme, {themeColors} from '../../theme/Theme';
import {useAutoForm} from '../AutoForm/useAutoForm';
import {UPDATES} from '../../shared/UPDATES';
import {uuidRegex} from './uuidRegex';

const classes = {
  root: {
    flexGrow: 1,
  },

  title: {
    ...theme.typography.button,
    color: theme.palette.text.primary,
    fontSize: '14px',
    fontWeight: 'bold',
  },

  value: {
    ...theme.typography.button,
    color: theme.palette.text.primary,
    fontSize: '14px',
  },
};

export function PrecursorWizardSummary({
  formFields,
  previousStep,
}: {
  formFields: WizardField<precursorAndHeaderFieldsType>[];
  previousStep: () => void;
}) {
  const {reviewDetails} = useReviewContext();

  const {
    values,
    submitForm,
    isSubmitting: isAboutToSubmit,
    setFieldValue,
  } = useAutoForm<precursorAndHeaderFieldsType>();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const fields = formFields.filter(
    (field) => get(values, field.id) !== undefined && field.enabled(values)
  );
  const genericFields = fields.filter((x) => !uuidRegex.test(x.id));
  const reviewReadyForHeaderPage =
    getBookedDate(values) !== null ||
    getReviewAction(values) === ReviewAction.CLIENT_DECLINED;

  return (
    <Box ml={2} mt={2}>
      <Typography variant="h4" pb={3}>
        Meeting Summary:
      </Typography>
      <Grid
        className="generic-fields"
        container
        mb={5}
        maxWidth={1200}
        columnSpacing={5}
        direction="row"
        flexWrap="wrap"
        pr={4}
      >
        <Grid item xs={6} direction="column">
          <Grid className="answer-wrapper" container>
            <Grid item xs={6} pt={2}>
              <Typography sx={classes.title}>Review Type</Typography>
            </Grid>
            <Grid
              className="answer"
              item
              padding={1}
              paddingBottom={2}
              paddingTop={2}
              xs={6}
            >
              <Typography sx={classes.value}>{reviewDetails?.reviewType}</Typography>
            </Grid>
          </Grid>
          <Divider flexItem className="divider" />
        </Grid>

        {genericFields.map((f, index) => (
          <Grid item xs={6} key={f.id}>
            <SubmitRow
              onClick={() => setFieldValue(UPDATES._3204, 'step', index)}
              key={f.id}
              label={f.label}
              title={f.title}
              answer={getLabelOrValue(values as any, f)}
            />
            <Divider flexItem className="divider" />
          </Grid>
        ))}
      </Grid>
      {reviewReadyForHeaderPage === false ? (
        <Grid item xs={8} justifyContent="center" alignItems="center">
          <Box
            padding={2}
            mt={6}
            mb={3}
            border={`2px solid ${themeColors.lightWarning}`}
            borderRadius="3px"
            display="flex"
            bgcolor="white"
          >
            <Warning color="warning" />
            <Typography variant="h6" ml={2}>
              Note, without a booked date this review cannot proceed to resilience page.
            </Typography>
          </Box>
        </Grid>
      ) : null}
      <Grid item container justifyContent="center" alignItems="center">
        <Grid item xs={8}>
          <Button variant="contained" color="secondary" onClick={previousStep}>
            Back
          </Button>
        </Grid>
        <Grid item xs={3}>
          <Button
            variant="contained"
            color="primary"
            onClick={() => {
              setIsSubmitting(true);
              submitForm().finally(async () => {
                setIsSubmitting(false);
              });
            }}
            data-test="Submit Review"
            disabled={isSubmitting}
          >
            {isAboutToSubmit ? 'Loading...' : 'Submit'}
          </Button>
        </Grid>
      </Grid>
    </Box>
  );
}
